import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FilePdf } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { API, api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { PageTitle, Stat, Empty } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { FlightRow, FlightSummary } from "@/pages/app/FlightLog";

const Th = ({ cols }) => <thead className="bg-[#F4F6FA] text-left text-xs uppercase tracking-wider text-[#475569]"><tr>{cols.map((c) => <th key={c} className="p-3">{c}</th>)}</tr></thead>;

const DisputeDetail = ({ id, onDone }) => {
  const { data: d, reload } = useFetch(`/admin/disputes/${id}`);
  if (!d) return <Loader />;
  const p = d.pack;
  const submit = async () => {
    if (!window.confirm("Submit this evidence to Stripe now? You can only submit once.")) return;
    try { await api.post(`/admin/disputes/${id}/submit`); toast.success("Evidence submitted to Stripe"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = [["Signatures", p.signatures.length], ["Emails sent", p.emails.length], ["Tickets checked in", p.tickets.filter((t) => t.checked_in_at).length + "/" + p.tickets.length], ["Quiz attempts", p.quiz_attempts.length], ["Days materials accessed", p.materials_accessed.length], ["Flight log entries", p.flight_log.length], ["Support tickets", p.support.length], ["Certificates", p.certificates.length]];
  return (
    <div className="panel mt-3 space-y-3 p-4 text-sm" data-testid="dispute-detail">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{rows.map(([k, v]) => <div key={k}><div className="text-xs uppercase text-[#475569]">{k}</div><div className="font-mono-num text-lg">{v}</div></div>)}</div>
      <div className="flex flex-wrap gap-2">
        <a className="btn-outline" href={`${API}/admin/disputes/${id}/pack.pdf`} target="_blank" rel="noreferrer" data-testid="dispute-pdf-link"><FilePdf /> Evidence pack PDF</a>
        <button className="btn-outline" onClick={async () => { await api.post(`/admin/disputes/${id}/rebuild`); toast.success("Pack rebuilt"); reload(); }} data-testid="dispute-rebuild-btn">Rebuild pack</button>
        {d.status === "needs_response" && <button className="btn-navy" onClick={submit} data-testid="dispute-submit-btn">Submit to Stripe</button>}
      </div>
    </div>
  );
};

export function Disputes() {
  const { data, loading, reload } = useFetch("/admin/disputes");
  const [open, setOpen] = useState(null);
  return (
    <>
      <PageTitle title="Chargebacks" sub="An evidence pack is built automatically when a card payment is disputed. Review it and submit before the deadline." />
      {loading ? <Loader /> : data.length === 0 ? <Empty>No disputes. Packs appear here automatically when Stripe reports one.</Empty> : data.map((d) => {
        const late = d.status === "needs_response" && new Date(d.due_by) - Date.now() < 3 * 86400000;
        return (
          <div key={d.id} className="panel mb-2 p-4 text-sm" data-testid={`dispute-row-${d.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span><strong>{d.order_number}</strong> · {d.user_email} · {money(d.amount_cents)} · {d.reason}</span>
              <span className="flex items-center gap-2"><span className={`text-xs font-bold uppercase ${late ? "text-[#C8382E]" : ""}`}>Due {fmtDateTime(d.due_by)}</span><span className="rounded-full bg-[#F4F6FA] px-2 py-0.5 text-xs font-bold uppercase">{d.status.replace("_", " ")}</span>
                <button className="text-xs underline" onClick={() => setOpen(open === d.id ? null : d.id)} data-testid={`dispute-open-${d.id}`}>{open === d.id ? "Close" : "Review"}</button></span>
            </div>
            {open === d.id && <DisputeDetail id={d.id} onDone={() => { setOpen(null); reload(); }} />}
          </div>
        );
      })}
    </>
  );
}

export function GiftCardsAdmin() {
  const { data, loading } = useFetch("/admin/gift-cards");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Gift cards" sub="Scholarship codes are managed under Coupons (type: scholarship)." />
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4"><Stat label="Cards issued" value={data.cards.length} testid="gc-stat-count" /><Stat label="Outstanding balance" value={money(data.outstanding_cents)} accent="#C8382E" testid="gc-stat-outstanding" /></div>
      {data.cards.length === 0 ? <Empty>No gift cards sold yet.</Empty> : (
        <div className="panel overflow-x-auto"><table className="w-full text-sm"><Th cols={["Code", "Recipient", "Bought by", "Value", "Balance", "Activity"]} /><tbody>
          {data.cards.map((c) => <tr key={c.id} className="border-t" data-testid={`gc-row-${c.id}`}><td className="p-3 font-mono-num">{c.code}</td><td className="p-3">{c.recipient_name}<div className="text-xs text-[#475569]">{c.recipient_email}</div></td><td className="p-3 text-xs">{c.buyer_email}</td><td className="p-3 font-mono-num">{money(c.initial_cents)}</td><td className="p-3 font-mono-num">{money(c.balance_cents)}</td><td className="p-3 text-xs">{c.history.map((h, i) => <div key={i}>{h.type} {money(Math.abs(h.amount_cents))} · {fmtDate(h.at)}</div>)}</td></tr>)}
        </tbody></table></div>
      )}
    </>
  );
}

const RateTable = ({ title, rows }) => (
  <div className="panel overflow-x-auto"><div className="eyebrow p-3">{title}</div><table className="w-full text-sm"><Th cols={["Name", "Expected", "Attended", "No-show", "Released early", "No-show rate"]} /><tbody>
    {rows.map((r) => <tr key={r.name} className="border-t"><td className="p-3">{r.name}</td><td className="p-3 font-mono-num">{r.expected}</td><td className="p-3 font-mono-num">{r.attended}</td><td className="p-3 font-mono-num">{r.no_show}</td><td className="p-3 font-mono-num">{r.released}</td><td className="p-3 font-mono-num font-semibold">{r.rate != null ? `${r.rate}%` : "–"}</td></tr>)}
  </tbody></table>{rows.length === 0 && <p className="p-3 text-sm text-[#475569]">No finished events yet.</p>}</div>
);

export function NoShows() {
  const { data, loading } = useFetch("/admin/reports/no-shows");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="No-show report" sub="Finished events only. A no-show is a confirmed ticket that never checked in. Released seats are people who told us they couldn't come." />
      <div className="grid gap-4 lg:grid-cols-2" data-testid="noshow-report"><RateTable title="By city" rows={data.by_city} /><RateTable title="By ticket type" rows={data.by_tier} /></div>
    </>
  );
}

export function FlightLogsAdmin() {
  const { data, loading } = useFetch("/admin/flight-logs");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Flight logs" sub="Review your students' demo flights and leave feedback." />
      {data.length === 0 ? <Empty>No flights logged yet.</Empty> : (
        <div className="panel overflow-x-auto"><table className="w-full text-sm"><Th cols={["Student", "Flights", "Without feedback", "Last flight"]} /><tbody>
          {data.map((r) => <tr key={r.user_id} className="border-t" data-testid={`flightlog-row-${r.user_id}`}><td className="p-3"><Link to={`/superadmin/flight-logs/${r.user_id}`} className="font-semibold underline">{r.name}</Link><div className="text-xs text-[#475569]">{r.email}</div></td><td className="p-3 font-mono-num">{r.flights}</td><td className="p-3 font-mono-num">{r.uncommented}</td><td className="p-3">{fmtDateTime(r.last)}</td></tr>)}
        </tbody></table></div>
      )}
    </>
  );
}

const CommentBox = ({ fid, onDone }) => {
  const [t, setT] = useState("");
  const send = async () => { try { await api.post(`/admin/flight-logs/flights/${fid}/comment`, { body: t }); setT(""); toast.success("Comment added"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return <div className="mt-2 flex gap-2"><Input placeholder="Instructor comment" value={t} onChange={(e) => setT(e.target.value)} data-testid={`comment-input-${fid}`} /><button className="btn-outline" disabled={!t.trim()} onClick={send} data-testid={`comment-send-${fid}`}>Send</button></div>;
};

export function FlightLogStudent() {
  const { uid } = useParams();
  const { data, loading, reload } = useFetch(`/admin/flight-logs/${uid}`);
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title={`Flight log: ${data.student.name}`} sub={data.student.email}><Link to="/superadmin/flight-logs" className="btn-outline">Back</Link></PageTitle>
      <FlightSummary s={data.summary} />
      <div className="mt-6 space-y-2">{data.flights.map((x) => <FlightRow key={x.id} x={x} extra={<CommentBox fid={x.id} onDone={reload} />} />)}</div>
    </>
  );
}
